import React from 'react';
import './SearchSelect.css';

/** `group` heads a run of consecutive options (in the order given) with that label. */
export type SearchSelectOption = { id: string; label: string; detail?: string; group?: string };

type Props = {
  id: string;
  label: string;
  /** The chosen option; it stays shown while the matches change. */
  value: SearchSelectOption | null;
  /** Matches for `search`. The caller searches (usually on the server) and pages. */
  options: SearchSelectOption[];
  search: string;
  onSearchChange: (search: string) => void;
  onChange: (option: SearchSelectOption) => void;
  placeholder?: string;
  /** True until `options` match the current `search`, including while typing pauses. */
  loading?: boolean;
  /** A failed search, shown in the list with a Retry button. */
  error?: string;
  onRetry?: () => void;
  emptyText?: string;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  loadMoreLabel?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
  inputClassName?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  /** Marks the typed text inside each option's label. */
  highlight?: boolean;
  /** Rendered under the field, e.g. its validation message. */
  children?: React.ReactNode;
};

type OptionRun = { group: string | undefined; options: Array<{ option: SearchSelectOption; index: number }> };

/** Consecutive options that share a `group` render under one heading; ungrouped options render as before. */
function groupRuns(options: SearchSelectOption[]): OptionRun[] {
  const runs: OptionRun[] = [];
  options.forEach((option, index) => {
    const last = runs[runs.length - 1];
    if (last && last.group === option.group) last.options.push({ option, index });
    else runs.push({ group: option.group, options: [{ option, index }] });
  });
  return runs;
}

/** The label with the first case-insensitive match of the search marked. */
function markMatch(label: string, search: string): React.ReactNode {
  const term = search.trim();
  const at = term ? label.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (at < 0) return label;
  return <>{label.slice(0, at)}<mark className="search-select__match">{label.slice(at, at + term.length)}</mark>{label.slice(at + term.length)}</>;
}

/** One field to type, filter and choose, in place of a search box paired with a select. */
export default function SearchSelect({
  id, label, value, options, search, onSearchChange, onChange, placeholder, loading = false, error, onRetry, emptyText = 'No matches',
  hasMore = false, loadingMore = false, onLoadMore, loadMoreLabel = 'Load more', disabled = false, required = false,
  invalid = false, describedBy, inputClassName, inputRef, highlight = false, children,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const input = React.useRef<HTMLInputElement | null>(null);
  const refocusing = React.useRef(false);
  const listId = `${id}-list`;
  const isOpen = open && !disabled;
  const activeIndex = Math.min(active, options.length - 1);
  const optionId = (option: SearchSelectOption) => `${id}-option-${option.id}`;
  const activeId = isOpen && activeIndex >= 0 ? optionId(options[activeIndex]) : undefined;
  const firstId = options[0]?.id;
  const valueId = value?.id;
  // A new search starts from the top; a loaded page appends and keeps the place.
  React.useEffect(() => { setActive(0); }, [search, firstId]);
  // Closed with focus, the field shows the choice selected, so typing replaces it rather than appending.
  React.useEffect(() => { if (!isOpen && document.activeElement === input.current) input.current?.select(); }, [isOpen, valueId]);
  const setInput = React.useCallback((node: HTMLInputElement | null) => {
    input.current = node;
    if (typeof inputRef === 'function') inputRef(node); else if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
  }, [inputRef]);
  // A reopened list starts at its first row, never at a row hovered last time and now out of view.
  const show = () => { if (!isOpen) { onSearchChange(''); setActive(0); setOpen(true); } };
  const choose = (option: SearchSelectOption) => { setOpen(false); onSearchChange(''); onChange(option); };
  // Escape from a button in the list returns focus to the field without reopening the list.
  const close = () => { setOpen(false); refocusing.current = true; input.current?.focus(); refocusing.current = false; };
  const move = (step: number) => {
    if (!options.length) return;
    const next = Math.max(0, Math.min(options.length - 1, activeIndex + step));
    setActive(next);
    // Only keyboard moves scroll the list; hovering never jumps it.
    document.getElementById(optionId(options[next]))?.scrollIntoView?.({ block: 'nearest' });
  };
  return <div className="search-select" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <label htmlFor={id}>{label}</label>
    <div className="search-select__control">
      <input ref={setInput} id={id} className={inputClassName} type="text" role="combobox" autoComplete="off" spellCheck={false}
        value={isOpen ? search : value?.label ?? ''} placeholder={(isOpen && value?.label) || placeholder} disabled={disabled}
        aria-expanded={isOpen} aria-controls={isOpen ? listId : undefined} aria-autocomplete="list" aria-required={required || undefined}
        aria-activedescendant={activeId} aria-invalid={invalid || undefined} aria-describedby={describedBy}
        onFocus={() => { if (!refocusing.current) show(); }} onClick={show}
        onChange={event => { onSearchChange(event.target.value); setOpen(true); }}
        onKeyDown={event => {
          if (event.nativeEvent.isComposing) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!isOpen) { show(); return; }
            move(event.key === 'ArrowDown' ? 1 : -1);
          } else if (event.key === 'Enter') {
            // Enter chooses; it never submits the surrounding form from here. While a search is
            // still loading, the list shows the previous matches, so Enter waits for the new ones.
            event.preventDefault();
            if (!isOpen) show(); else if (!loading && activeIndex >= 0) choose(options[activeIndex]);
          } else if (event.key === 'Escape' && isOpen) {
            // Close the list only; the dialog around it stays open.
            event.preventDefault(); event.stopPropagation(); setOpen(false);
          }
        }} />
      {isOpen ? <div className="search-select__popup" data-loading={loading || undefined} onMouseDown={event => event.preventDefault()}
        onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); } }}>
        <div id={listId} role="listbox" aria-label={label} aria-busy={loading || undefined}>
          {groupRuns(options).map(run => {
            const rows = run.options.map(({ option, index }) => <div key={option.id} id={optionId(option)} role="option" aria-selected={value?.id === option.id}
              className={`search-select__option${index === activeIndex ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(index)} onClick={() => choose(option)}>
              <span>{highlight ? markMatch(option.label, search) : option.label}</span>{option.detail ? <span className="search-select__detail">{option.detail}</span> : null}
            </div>);
            if (run.group === undefined) return <React.Fragment key={`run-${run.options[0].index}`}>{rows}</React.Fragment>;
            const headingId = `${id}-group-${run.options[0].index}`;
            return <div key={headingId} role="group" aria-labelledby={headingId} className="search-select__group">
              <div id={headingId} className="search-select__group-label" role="presentation">
                <span>{run.group}</span><span className="search-select__group-count">{run.options.length}</span>
              </div>
              {rows}
            </div>;
          })}
        </div>
        {error ? <p className="search-select__status search-select__status--error" role="alert">{error}</p>
          : loading ? <p className="search-select__status" role="status">{options.length ? 'Searching…' : 'Loading…'}</p>
            : !options.length ? <p className="search-select__status" role="status">{emptyText}</p> : null}
        {error && onRetry ? <button type="button" className="btn subtle xs search-select__more" onClick={onRetry}>Retry</button> : null}
        {hasMore && onLoadMore ? <button type="button" className="btn subtle xs search-select__more" disabled={loadingMore} onClick={onLoadMore}>
          {loadingMore ? 'Loading…' : loadMoreLabel}</button> : null}
      </div> : null}
    </div>
    {children}
  </div>;
}
